// src/pages/services/PillLowDosePage.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaPills, FaInfoCircle, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { goReserve } from '../../utils/navigation';
import { useMedicinePrices, toSinglePriceItems } from '../../hooks/useMedicinePrices';

interface PillChildProps { ipAllowed: boolean }

type NoticeDoc = { title?: string; html?: string };

const NOTICE_VERSION = 'v2025-10-01';

function getPublicBase(): string {
  // @ts-ignore
  // eslint-disable-next-line
  const viteBase = process.env.PUBLIC_URL || '';
  // @ts-ignore
  const craBase = (typeof process !== 'undefined' && process?.env?.PUBLIC_URL) || '';
  const base = viteBase || craBase || '/';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}
function normalizeLang(input: string): 'ja' | 'zh' {
  return String(input || '').toLowerCase().startsWith('zh') ? 'zh' : 'ja';
}

const PillLowDosePage: React.FC<PillChildProps> = ({ ipAllowed }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang: 'ja' | 'zh' = normalizeLang(i18n.language);

  // 薬品価格（DB優先、フォールバックは静的データ）※避妊ピルは全ページ共通の service_type 'pill'
  const { medicines } = useMedicinePrices('pill', lang);

  // 注意事項モーダル（仅低剂量）
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [agree, setAgree] = useState(false);
  const [doc, setDoc] = useState<NoticeDoc | null>(null);
  const [docLoading, setDocLoading] = useState(false);

  const openNotice = async () => {
    setAgree(false);
    setNoticeOpen(true);
    try {
      setDocLoading(true);
      const base = getPublicBase();
      const primary = `${base}/data/notices/pill_low.${lang}.json?v=${encodeURIComponent(NOTICE_VERSION)}`;
      const fallback = `${base}/data/notices/pill_low.${lang === 'ja' ? 'zh' : 'ja'}.json?v=${encodeURIComponent(
        NOTICE_VERSION
      )}`;

      const tryFetch = async (url: string) => {
        const res = await fetch(url, { cache: 'no-cache' });
        return res.ok ? ((await res.json()) as NoticeDoc) : null;
      };
      let json = await tryFetch(primary);
      if (!json) json = await tryFetch(fallback);
      setDoc(json || { title: lang === 'ja' ? '注意事項' : '注意事项', html: '' });
    } finally {
      setDocLoading(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNoticeOpen(false); };
    if (noticeOpen) {
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [noticeOpen]);

  /** 同意后进入预约（统一 pill，scope = pill_low） */
  const proceedReserve = () => {
    if (!agree) return;
    setNoticeOpen(false);
    goReserve(navigate, ipAllowed, {
      preselectService: 'pill',
      preAgreed: true,
      from: 'service',
      pillNoticeScope: 'pill_low',
    });
  };

  const L = {
    ja: {
      helmetTitle: '低用量ピル・避妊 - Melodis Clinic',
      back: '戻る',
      heading: '低用量ピル・避妊',
      badges: ['診察料 0円', '24時間オンライン予約', 'スピード発送', '自由診療'],
      overview: '概要',
      p1: '低用量ピルは女性ホルモンの作用で排卵を抑制し、正しく毎日服用することで避妊効果は高く（概ね99％台）とされています。避妊に加えて、月経移動・生理痛の軽減・出血量の減少・周期の安定・期間短縮・PMSやにきびの改善などが期待できます。',
      p2: '当院ではトリキュラー®（28）やマーベロン®など、ホルモン量や特性が異なる薬剤を取り扱っています。オンライン診療で体質・既往歴を確認のうえ、医師が適切なお薬をご提案します。',
      footnote: '※公的医療保険の適用外（自由診療）です。服用中の薬がある方、喫煙・血栓症リスク等のある方は必ずご申告ください。',
      priceTitle: '価格（診察料 0円）：',
      priceGroups: {
        single: '【シート単体】',
        note: '数か月分をまとめて処方することも可能です。'
      },
      priceItems: [
        { name: 'トリキュラー (Triquilar)', dosage: '1シート (28日分)', price: '3,200', taxPrice: '3,520' },
        { name: 'マーベロン (Marvelon)', dosage: '1シート (28日分)', price: '3,200', taxPrice: '3,520' },
      ],
      faqBtn: 'FAQを見る',
      noticeBtn: '注意事項はこちらから',
      backToList: 'ピル一覧へ戻る',
      reserveCta: 'オンライン診療を予約する',
      doctorLine: '当院医師はピル処方の豊富な経験があり、「緊急避妊薬の処方にかかるオンライン診療研修」も修了しています。',
      modalAgree: '上記を読み、内容に同意します。',
      modalCta: '同意して予約に進む',
      modalClose: '閉じる',
    },
    zh: {
      helmetTitle: '低剂量避孕药・避孕 - Melodis Clinic',
      back: '返回',
      heading: '低剂量避孕药・避孕',
      badges: ['诊察费 0 日元', '24 小时在线预约', '急速发货', '自费诊疗'],
      overview: '概览',
      p1: '低剂量避孕药通过调节雌孕激素抑制排卵；若每日规范服用，避孕有效率可达 99% 左右。除避孕外，还可用于经期调整、缓解痛经、减少经量、稳定周期、缩短经期，并有助于改善经前综合征（PMS）与痤疮等问题。',
      p2: '本院提供多种不同剂量与特性的药物（如 Triquilar®、Marvelon® 等）。医生将结合体质与既往史，通过在线问诊为您推荐更合适的方案。',
      footnote: '※自费诊疗（不适用公费保险）。如正在服用其他药物、吸烟或存在血栓风险等，请务必告知。',
      priceTitle: '价格（诊察费 0 日元）：',
      priceGroups: {
        single: '【单版】',
        note: '亦可按需一次性开具数个月的处方。'
      },
      priceItems: [
        { name: '特洁乐 (Triquilar)', dosage: '1版 (28日份)', price: '3,200', taxPrice: '3,520' },
        { name: '妈富隆 (Marvelon)', dosage: '1版 (28日份)', price: '3,200', taxPrice: '3,520' },
      ],
      faqBtn: '查看 FAQ',
      noticeBtn: '查看注意事项',
      reserveCta: '立即预约在线咨询',
      backToList: '返回避孕药一览',
      doctorLine: '本院医师具有丰富的避孕药处方经验，并已完成“在线诊疗开具紧急避孕药”相关培训。',
      modalAgree: '我已阅读并同意上述内容。',
      modalCta: '同意并继续预约',
      modalClose: '关闭',
    },
  } as const;

  // 価格表は DB(admin設定)優先。'pill' サービスの薬品から本ページの薬(トリキュラー/マーベロン)だけ名称で抽出。
  // price/taxPrice は本ページJSXが文字列前提のため 'ja-JP' 整形。空ならハードコード L[lang].priceItems にフォールバック。
  const dbPriceItems = toSinglePriceItems(medicines, lang)
    .filter((it) => /トリキュラー|マーベロン|特洁乐|妈富隆|Triquilar|Marvelon/i.test(it.name))
    .map((it) => ({ ...it, price: it.price.toLocaleString('ja-JP'), taxPrice: it.taxPrice.toLocaleString('ja-JP') }));
  const priceItems: any[] = dbPriceItems.length > 0 ? dbPriceItems : [...L[lang].priceItems];

  return (
    <div className="pt-0 sm:pt-10 pb-10 sm:pb-20 bg-[#fff9f8] font-sans" translate="no">
      <Helmet>
        <title>{L[lang].helmetTitle}</title>
        <meta name="description" content={t('seo.pill_low_dose.description')} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">

          {/* 上部：戻る / 返回 */}
          <div className="mb-4">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm"
            >
              <FaArrowLeft className="w-4 h-4" /> {L[lang].back}
            </button>
          </div>

          {/* 見出し / 标题 */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-rose-100 p-3">
              <FaPills className="text-rose-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              <span className="text-rose-400 mr-2">│</span>{L[lang].heading}
            </h1>
          </div>

          <div className="mb-8 flex items-center gap-3 text-left">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-1.5 text-xs md:text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {t('seo.supervision_doctor')}
              <span className="text-[10px] text-slate-400 font-normal ml-1">{t('seo.supervision_title')}</span>
            </Link>
          </div>

          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8 bg-[#fff9f8]/50 border-l-4 border-sky-200 pl-4 py-2">
            {t('seo.pill_low_dose.summary')}
          </p>

          {/* 概要ブロック / 概览区块 */}
          <section className="rounded-2xl border border-rose-100 bg-rose-50/60 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FaInfoCircle className="text-rose-600 w-5 h-5" />
              <h2 className="text-2xl font-semibold text-rose-700">{L[lang].overview}</h2>
            </div>

            {/* badges */}
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
              {L[lang].badges.map((b, i) => (
                <li key={i} className="rounded-xl border border-rose-200 bg-white px-4 py-3 text-slate-800 text-center shadow-sm">
                  {b}
                </li>
              ))}
            </ul>

            <div className="space-y-4 text-slate-800 leading-relaxed">
              <p>{L[lang].p1}</p>
              <p>{L[lang].p2}</p>
              <p className="text-slate-600 text-sm">{L[lang].footnote}</p>
            </div>

            {/* 料金とリンク */}
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 md:p-8">
              <h3 className="text-xl font-semibold text-slate-800 mb-6">{L[lang].priceTitle}</h3>
              
              <div className="space-y-8">
                <div>
                  <h4 className="font-bold text-rose-700 mb-3 border-b border-rose-100 pb-1">{L[lang].priceGroups.single}</h4>
                  <ul className="space-y-4 text-slate-700">
                    {priceItems.map((item: any, i: number) => (
                      <li key={i} className="flex justify-between items-start flex-wrap gap-2 py-1 border-b border-slate-50 last:border-0 pb-3">
                        <span className="flex flex-col">
                          <span className="font-bold text-slate-800">{item.name}</span>
                          <span className="text-[11px] text-slate-500">{item.dosage}</span>
                        </span>
                        <div className="text-right">
                          <div className="flex items-baseline gap-1 justify-end">
                            <span className="font-bold text-lg text-slate-900">{item.price}</span>
                            <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                          </div>
                          <div className="text-[10px] text-slate-400 font-normal">
                            ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice}{lang === 'ja' ? '円' : ' 日元'})
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('/faq/pill')}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-[#fff9f8] px-6 py-2.5 text-sm font-medium text-sky-700 hover:bg-sky-100 transition"
                >
                  {L[lang].faqBtn} <FaArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={openNotice}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-300 bg-rose-50 px-6 py-2.5 text-sm font-medium text-rose-700 hover:bg-rose-100 transition"
                >
                  {L[lang].noticeBtn} <FaArrowRight className="w-4 h-4" />
                </button>
              </div>

              <p className="mt-4 text-slate-500 text-xs">{L[lang].priceGroups.note}</p>
            </div>

            <div className="mt-6 text-slate-600 text-sm">
              {L[lang].doctorLine}
            </div>
          </section>

          {/* 下部：戻る／予約 CTA（→ モーダル経由） */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/menu/pill')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-slate-700 hover:bg-slate-50"
            >
              <FaArrowLeft className="w-4 h-4" /> {L[lang].backToList}
            </button>
            {isAuthenticated && (
              <button
                onClick={openNotice}
                className="inline-block bg-rose-500 hover:bg-rose-600 active:bg-rose-700 text-white px-8 py-3 rounded-full font-semibold shadow-sm transition"
              >
                {L[lang].reserveCta}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 注意事項モーダル（pill_low） */}
      {noticeOpen && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          onClick={() => setNoticeOpen(false)}
        >
          <div className="fixed inset-0 bg-black/40" />
          <div
            className="min-h-full flex items-start md:items-center justify-center p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1001] w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6 md:p-7 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 id="notice-title" className="text-xl md:text-2xl font-semibold text-slate-900">
                  {doc?.title || (lang === 'ja' ? '注意事項' : '注意事项')}
                </h3>
                <button
                  onClick={() => setNoticeOpen(false)}
                  aria-label="Close"
                  className="rounded-full px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  ×
                </button>
              </div>

              <div className="prose prose-slate max-w-none text-[15px] leading-relaxed">
                {docLoading ? (
                  <p className="text-slate-500">{lang === 'ja' ? '読み込み中…' : '加载中…'}</p>
                ) : doc?.html ? (
                  <div dangerouslySetInnerHTML={{ __html: doc.html }} />
                ) : (
                  <p className="text-slate-500">
                    {lang === 'ja' ? 'この項目の注意事項は現在準備中です。' : '该项目的注意事项正在准备中。'}
                  </p>
                )}
              </div>

              <label className="mt-4 flex items-start gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{L[lang].modalAgree}</span>
              </label>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled={!agree}
                  onClick={proceedReserve}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 font-semibold text-white transition ${agree ? 'bg-rose-500 hover:bg-rose-600 active:bg-rose-700' : 'bg-rose-300 cursor-not-allowed'
                    }`}
                >
                  {L[lang].modalCta}
                </button>
                <button
                  onClick={() => setNoticeOpen(false)}
                  className="rounded-full border border-slate-300 px-6 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PillLowDosePage;
